import { Button, Grid, TextField, Box, Typography, Paper, Divider, useMediaQuery, LinearProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import Attribute from "./Attribute";
import AnnonceContext from "../../../contexts/AnnonceContext";
import Upload from "../publier/FormComponents/Forms/upload";
import { updateAnnonce, updateAnnonceImage } from "../../../service/annonceApi";
import { notifySuccess } from "src/service/notificationService";
import { styled } from '@mui/material/styles';

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
}));

const FormSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

// Fonction utilitaire pour compresser une image
const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculer les nouvelles dimensions
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convertir en blob avec compression
      canvas.toBlob((blob) => {
        resolve(new File([blob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        }));
      }, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Fonction pour uploader une image avec retry
const uploadImageWithRetry = async (annonceId, imageFile, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Tentative d'upload ${attempt}/${maxRetries}`);
      
      // Compresser l'image avant upload
      const compressedImage = await compressImage(imageFile);
      
      const response = await updateAnnonceImage(annonceId, compressedImage);
      console.log('Upload réussi:', response);
      return response;
      
    } catch (error) {
      lastError = error;
      console.error(`Erreur upload tentative ${attempt}:`, error);
      
      if (attempt < maxRetries) {
        // Attendre avant de réessayer (backoff exponentiel)
        const delay = Math.pow(2, attempt) * 1000;
        console.log(`Nouvelle tentative dans ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw new Error(`Échec de l'upload après ${maxRetries} tentatives: ${lastError?.message || 'Erreur inconnue'}`);
};

export default function UpdateAds({ selectedAnnonce, setSelectedAnnonce, userToken = "", categories, updateUserAds }) {

    const [imageUpload, setImageUpload] = useState();
    const [currentCategory, setCurrentCategory] = useState(null);
    const [formAttributes, setFormAttributes] = useState([]);
    const [date, setDate] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formAnnonce, setFormAnnonce] = useState({
      id: selectedAnnonce.id,
      origin: selectedAnnonce.origin,
      status: selectedAnnonce.status,
      ville: selectedAnnonce.ville,
      details: selectedAnnonce.details,
    });

    const [isComplete, setIsComplete] = useState(false);

    const handleSubmit = async () => {
      if (isSubmitting) return; // Éviter les soumissions multiples
      
      try {
        setIsSubmitting(true);
        setUploadProgress(0);
        
        // Étape 1: Mettre à jour l'annonce
        setUploadProgress(20);
        const response = await updateAnnonce(formAnnonce.id, formAnnonce);
        const ads = response;
        const annonceId = response.id;
        ads.annonceAttributes = Object.values(ads.annonceAttributes);
        setUploadProgress(50);

        // Étape 2: Upload de l'image si présente
        const imageFile = imageUpload && imageUpload.file ? imageUpload.file : imageUpload;
        if (imageFile) {
          setUploadProgress(60);
          try {
            await uploadImageWithRetry(annonceId, imageFile);
            setUploadProgress(100);
          } catch (uploadError) {
            console.error('Erreur lors de l\'upload de l\'image:', uploadError);
            notifySuccess('Annonce mise à jour mais l\'image n\'a pas pu être uploadée. Vous pourrez la modifier plus tard.');
          }
        } else {
          setUploadProgress(100);
        }

        // Mettre à jour l'interface
        updateUserAds(ads);
        setSelectedAnnonce(ads);
        notifySuccess('Annonce mise à jour avec succès');
        
      } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'annonce:', error);
        notifySuccess('Erreur lors de la mise à jour de l\'annonce. Veuillez réessayer.');
      } finally {
        setIsSubmitting(false);
        setUploadProgress(0);
      }
    }

    const setAnnonceAttributes = () => {
      const annonceAttributes = selectedAnnonce.annonceAttributes;
      const formAttributes = annonceAttributes.map((attribute) => {
        if (!attribute.uiType)
          attribute.uiType = "";
        return attribute;
      }
      );
      setFormAttributes(formAttributes);
    }

    useEffect(() => {
      const category = categories.find((category) => {
        return category.id === selectedAnnonce.category.id;
      })
      setCurrentCategory(category)
      setAnnonceAttributes()
    }, []);

    useEffect(() => {
      setFormAnnonce({
        ...formAnnonce,
        annonceAttributes: formAttributes,
      });
    }, [formAttributes]);

  const isMobile = useMediaQuery('(max-width:900px)');

  return (
    <Box sx={{ p: 3, pb: isMobile ? '80px' : 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Grid container spacing={3}>
        {currentCategory && (
          <Grid item xs={12} sx={{ pb: isMobile ? '90px' : 0 }}>
            <AnnonceContext.Provider
                value={{
                  formAnnonce: formAnnonce,
                  setFormAnnonce: setFormAnnonce,
                  currentCategory: currentCategory,
                  setCurrentCategory: setCurrentCategory,
                  formAttributes: formAttributes,
                  setFormAttributes: setFormAttributes,
                  handleSubmit: handleSubmit,
                  imageUpload: imageUpload,
                  setImageUpload: setImageUpload,
                  date: date,
                  setDate: setDate,
                  isComplete: isComplete,
                  setIsComplete: setIsComplete,
                  isSubmitting: isSubmitting,
                  uploadProgress: uploadProgress,
                }}
            >
              <FormSection>
                <SectionTitle>Informations publiques</SectionTitle>
                <Box sx={{ mb: 3 }}>
              <Attribute isSecret={false} selectedAnnonce={selectedAnnonce}/>
                </Box>
              </FormSection>

              <FormSection>
                <SectionTitle>Informations confidentielles</SectionTitle>
                <Box sx={{ mb: 3 }}>
              <Attribute isSecret={true} selectedAnnonce={selectedAnnonce}/>
                </Box>
              </FormSection>

              <FormSection>
                <SectionTitle>Photos</SectionTitle>
                <Box sx={{ mb: 3 }}>
                  <Upload />
                  
                  {/* Indicateur de progression pour l'upload */}
                  {isSubmitting && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Mise à jour en cours...
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadProgress} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#1b9faa',
                          }
                        }} 
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {uploadProgress}% terminé
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Bouton de sauvegarde */}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      sx={{
                        backgroundColor: '#1b9faa',
                        '&:hover': {
                          backgroundColor: '#15808a',
                        },
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {isSubmitting ? 'Mise à jour...' : 'Sauvegarder les modifications'}
                    </Button>
                  </Box>
                </Box>
              </FormSection>

              <FormSection>
                <SectionTitle>Détails supplémentaires</SectionTitle>
              <TextField
                  fullWidth
                  id="outlined-multiline-flexible"
                  label="Détails"
                  multiline
                  maxRows={4}
                  value={formAnnonce ? formAnnonce.details : ""}
                  onChange={(e) =>
                      setFormAnnonce({ ...formAnnonce, details: e.target.value })
                  }
                  sx={{ mb: 3 }}
              />
              </FormSection>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSubmit}
                  size="large"
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                    Mettre à jour votre annonce
                  </Button>
              </Box>
            </AnnonceContext.Provider>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
